import React from 'react';
import { CountryCode } from '@dayetopia/library/types';
import { Trans } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { TextVariant, Typography } from '@components/shared/design/Typography';
import { useCountry } from '@hooks/useCountry';
import Lightbulb from '@images/vms-account/vms-icons/Lightbulb.svg';
import { isFestivePeriod } from '@utils/helpers';

interface Props {
  small?: boolean;
  sampleTaken?: boolean;
  checkout?: boolean;
  marginBottom?: number;
  marginBottomMobile?: number;
}

const SampleHolidayNotice: React.FC<Props> = ({ small = false, sampleTaken = false, checkout = false, marginBottom, marginBottomMobile }) => {
  const showNotice = isFestivePeriod();
  const { country } = useCountry();

  if (!showNotice) return null;

  const lastDateForPosting = country === CountryCode.US ? '16th December' : '18th December';
  const resumeDate = country === CountryCode.US ? '6th January' : '2nd January';

  return (
    <OverviewItem $marginBottom={marginBottom} $marginBottomMobile={marginBottomMobile}>
      {!small ? (
        <ItemIcon>
          <Lightbulb aria-label="Holiday Notice Icon" />
        </ItemIcon>
      ) : null}
      <ItemDetails>
        <TitleRow>
          {small ? <Lightbulb aria-label="Holiday Notice Icon" /> : null}
          <Typography $variant={TextVariant.BodyBoldMD}>
            <Trans i18nKey="components.sample-holiday-notice.title" />
          </Typography>
        </TitleRow>
        <StyledTypography $variant={TextVariant.BodyRegularMD}>
          <Trans
            i18nKey={checkout ? 'components.sample-holiday-notice.checkout-message' : 'components.sample-holiday-notice.default-message'}
            values={{ lastDateForPosting, resumeDate }}
            components={{ span: <span /> }}
          />
        </StyledTypography>
        {sampleTaken && (
          <Typography $variant={TextVariant.BodyBoldMD}>
            <Trans i18nKey="components.sample-holiday-notice.sample-taken-message" />
          </Typography>
        )}
      </ItemDetails>
    </OverviewItem>
  );
};

export default SampleHolidayNotice;

const OverviewItem = styled.div<{ $marginBottom?: number; $marginBottomMobile?: number }>`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 20px 30px;
  gap: 20px;
  background: ${Colors.paleGold50};
  margin-bottom: ${({ $marginBottom }) => $marginBottom}px;

  @media (max-width: 768px) {
    padding: 15px 20px;
    gap: 15px;
    margin-bottom: ${({ $marginBottomMobile }) => $marginBottomMobile}px;
  }
`;

const ItemIcon = styled.div`
  width: 50px;
  height: 50px;
  background: ${Colors.paleGold};
  border: 2px solid #fffbe7;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const StyledTypography = styled(Typography)`
  & span {
    font-weight: bold;
  }
`;
